import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { EyeIcon, PencilIcon, PlusIcon, PrinterIcon, TrashIcon } from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  deleteRiskAssessment,
  exportRiskAssessmentPdf,
  exportRiskRegisterPdf,
  fetchRiskAssessments,
  fetchRiskCategories,
} from "../../../../api/hse/risk";
import { HseRowActionsMenu } from "../../../../components/dashboard/hse/HseRowActionsMenu";
import { RiskLevelBadge } from "../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { RiskSubnav } from "../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import {
  formatDisplayDate,
  isExcluded,
  RISK_LEVELS,
  RISK_STATUSES,
  riskApiError,
} from "../../../../components/dashboard/hse/risk/riskUtils";

export const HseKsRiskRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useUserHasAccess({ role: "hse_ks_risk", action: "create" });
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    query: "",
    risk_statuses: [],
    risk_categories: [],
    risk_levels: [],
  });
  const { run, isBusy, busyProps } = useHseBusyAction();

  useEffect(() => {
    fetchRiskCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchRiskAssessments({
        query: filters.query,
        status: filters.risk_statuses?.[0],
        hse_risk_category_id: filters.risk_categories?.[0],
        risk_level: filters.risk_levels?.[0],
        per_page: 100,
      });
      setItems(response.data || []);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.risk_statuses, filters.risk_categories, filters.risk_levels]);

  const active = items.filter((item) => !isExcluded(item));
  const excluded = items.filter((item) => isExcluded(item));

  const handleExportOne = (item) =>
    run(`pdf-${item.id}`, async () => {
      try {
        await exportRiskAssessmentPdf(item.id, `hse-risiko-${item.id}.pdf`);
        toast.success(t("hse.risk.pdf_ready"));
      } catch (err) {
        toast.error(err.message || t("hse.risk.pdf_failed"));
      }
    });

  const handleDelete = async (item) => {
    if (!window.confirm(t("hse.risk.delete_confirm", { title: item.title }))) return;
    try {
      await deleteRiskAssessment(item.id);
      toast.success(t("hse.risk.deleted"));
      load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
    }
  };

  const handleExportRegister = () =>
    run("register-pdf", async () => {
      try {
        await exportRiskRegisterPdf({
          query: filters.query,
          status: filters.risk_statuses?.[0],
          hse_risk_category_id: filters.risk_categories?.[0],
          risk_level: filters.risk_levels?.[0],
        });
        toast.success(t("hse.risk.pdf_ready"));
      } catch (err) {
        toast.error(err.message || t("hse.risk.pdf_failed"));
      }
    });

  const renderRow = (item) => (
    <li key={item.id} className="px-5 py-4 hover:bg-slate-50 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={`/dashboard/hse-ks/risk/${item.id}`} className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-900">{item.title}</p>
          <p className="mt-1 text-sm text-slate-500">
            {[
              item.category_name ||
                (item.category
                  ? t(`hse.risk.category.${item.category}`, { defaultValue: item.category })
                  : null),
              formatDisplayDate(item.updated_at),
              item.open_tiltak_count || item.tiltak?.length
                ? t("hse.risk.tiltak.count", { count: item.open_tiltak_count || item.tiltak.length })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </Link>
        <div className="flex items-center gap-2">
          <RiskLevelBadge level={item.risk_level} excluded={isExcluded(item)} />
          <HseRowActionsMenu
            variant="labeled"
            label={t("hse.risk.more")}
            header={t("hse.risk.more_header")}
            items={[
              {
                label: t("hse.risk.open"),
                icon: EyeIcon,
                action: () => navigate(`/dashboard/hse-ks/risk/${item.id}`),
              },
              {
                label: t("hse.risk.edit"),
                icon: PencilIcon,
                hidden: !canEdit,
                action: () => navigate(`/dashboard/hse-ks/risk/${item.id}/edit`),
              },
              {
                label: t("hse.risk.pdf"),
                icon: PrinterIcon,
                disabled: isBusy(),
                loading: isBusy(`pdf-${item.id}`),
                action: () => handleExportOne(item),
              },
              {
                label: t("hse.risk.delete"),
                icon: TrashIcon,
                danger: true,
                hidden: !canEdit,
                action: () => handleDelete(item),
              },
            ]}
          />
        </div>
      </div>
    </li>
  );

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.risk.register.title")}
      subtitle={t("hse.risk.register.subtitle")}
      listCount={loading ? null : active.length}
      mainButton={
        canCreate
          ? { label: t("hse.risk.new"), icon: PlusIcon, path: "/dashboard/hse-ks/risk/new" }
          : undefined
      }
      actions={[
        {
          label: t("hse.risk.pdf"),
          icon: PrinterIcon,
          action: handleExportRegister,
          hidden: loading || !!error,
          menu: false,
          ...busyProps("register-pdf"),
        },
      ]}
    >
      <HsePageBody>
        <RiskSubnav active="register" />

        <div>
          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("hse.risk.search")}
            initialFilters={filters}
            onFiltersChange={(next) =>
              setFilters({
                query: next.query || "",
                risk_statuses: next.risk_statuses || [],
                risk_categories: next.risk_categories || [],
                risk_levels: next.risk_levels || [],
              })
            }
            customDropdownFilters={[
              {
                key: "risk_statuses",
                label: t("hse.risk.fields.status"),
                options: RISK_STATUSES.map((status) => ({
                  value: status,
                  label: t(`hse.risk.status.${status}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
              {
                key: "risk_categories",
                label: t("hse.risk.fields.category"),
                options: categories.map((category) => ({
                  value: String(category.id),
                  label: category.name,
                  class: "bg-slate-100 text-slate-800",
                })),
              },
              {
                key: "risk_levels",
                label: t("hse.risk.fields.level"),
                options: RISK_LEVELS.map((level) => ({
                  value: level,
                  label: t(`hse.risk.level.${level}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
            ]}
          />
        </div>

        <HseListCard>
          {loading ? (
            <div className="p-8">
              <ResourceLoading size="base" />
            </div>
          ) : error ? (
            <ResourceError
              errors={[error]}
              onRetry={load}
            />
          ) : active.length === 0 ? (
            <div className="px-6 py-16">
              <ResourceEmpty message={t("hse.risk.empty")} />
              {canCreate ? (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/hse-ks/risk/new")}
                    className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
                  >
                    {t("hse.risk.new")}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">{active.map(renderRow)}</ul>
          )}
        </HseListCard>

        {excluded.length > 0 ? (
          <section className="mt-8 overflow-hidden rounded-[18px] border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="text-sm font-extrabold text-slate-900">{t("hse.risk.excluded.title")}</h3>
              <p className="mt-1 text-sm text-slate-500">{t("hse.risk.excluded.hint")}</p>
            </div>
            <ul className="divide-y divide-slate-100">{excluded.map(renderRow)}</ul>
          </section>
        ) : null}
      </HsePageBody>
    </HsePageChrome>
  );
};
